import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import PrivateRoute from './components/PrivateRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import LineSettings from './pages/LineSettings';
import ReservationsNew from './pages/ReservationsNew';
import Customers from './pages/Customers';
import CustomerDetail from './pages/CustomerDetail';
import Employees from './pages/Employees';
import Shifts from './pages/Shifts';
import Attendances from './pages/Attendances';
import StaffNotificationSettings from './pages/StaffNotificationSettings';
// Menus is deprecated, use Products instead
import ShopSettingsUnified from './pages/settings/ShopSettingsUnified';
import PlanSettings from './pages/settings/PlanSettings';
import AllShops from './pages/AllShops';
import SalesReport from './pages/SalesReport';
import TimeChargeSettings from './pages/settings/TimeChargeSettings';
import StripeConnectSettings from './pages/settings/StripeConnectSettings';
import MobileOrderSettings from './pages/settings/MobileOrderSettings';
import ProductCategories from './pages/ProductCategories';
import ProductOptions from './pages/ProductOptions';
import Products from './pages/Products';
import Tables from './pages/Tables';
import Printers from './pages/Printers';
import Plans from './pages/Plans';
import Orders from './pages/Orders';
import Checkout from './pages/Checkout';
import Analytics from './pages/Analytics';
import FloorMap from './pages/FloorMap';
import Blacklist from './pages/Blacklist';
import SelectShop from './pages/SelectShop';
import MigrateData from './pages/MigrateData';
import BackApproval from './pages/BackApproval';
import Receivables from './pages/Receivables';
import BottleKeeps from './pages/BottleKeeps';
import Payouts from './pages/Payouts';
import SetupCheck from './pages/SetupCheck';
import ProxyOrder from './pages/ProxyOrder';
import Payroll from './pages/Payroll';
import AllowanceTypes from './pages/AllowanceTypes';
import NotFound from './pages/NotFound';
import Events from './pages/Events';
import Coupons from './pages/Coupons';

// 言語プレフィックス付きURLを処理するコンポーネント
// /th/dashboard -> 言語をthに設定してから /dashboard にリダイレクト
/**
 * /ja/... のような言語プレフィックス付きURLで来たときに、
 * 言語を覚えてプレフィックスを外した場所へ移す。
 *
 * location.pathname は basename を除いた値なので、ここでは basename を意識しない。
 * URL側に prefix を書き戻す処理は LanguageContext にあり、
 * そちらは basename の内側に置くよう直してある。
 */
const LanguageRedirect: React.FC<{ lang: 'ja' | 'en' | 'th' }> = ({ lang }) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    localStorage.setItem('language', lang);

    const pathWithoutLang = location.pathname.replace(/^\/(ja|en|th)(?=\/|$)/, '') || '/dashboard';
    navigate(pathWithoutLang + location.search + location.hash, { replace: true });
  }, [lang, location, navigate]);

  return null;
};

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LanguageProvider>
        <AuthProvider>
          <Router basename={process.env.PUBLIC_URL || '/'}>
          <Routes>
            {/* Language prefix routes - must be first to catch /ja/*, /en/*, /th/* */}
            <Route path="/ja/*" element={<LanguageRedirect lang="ja" />} />
            <Route path="/en/*" element={<LanguageRedirect lang="en" />} />
            <Route path="/th/*" element={<LanguageRedirect lang="th" />} />

            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />

            {/* Shop selection route */}
            <Route
              path="/select-shop"
              element={
                <PrivateRoute>
                  <SelectShop />
                </PrivateRoute>
              }
            />

            {/* Private routes */}
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/line-settings"
              element={
                <PrivateRoute>
                  <Layout>
                    <LineSettings />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/customers"
              element={
                <PrivateRoute>
                  <Layout>
                    <Customers />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/customers/:customerId"
              element={
                <PrivateRoute>
                  <Layout>
                    <CustomerDetail />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/employees"
              element={
                <PrivateRoute>
                  <Layout>
                    <Employees />
                  </Layout>
                </PrivateRoute>
              }
            />
            {/* /staff-users は /employees に統合済み */}
            <Route path="/staff-users" element={<Navigate to="/employees" replace />} />
            <Route
              path="/shifts"
              element={
                <PrivateRoute>
                  <Layout>
                    <Shifts />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/attendances"
              element={
                <PrivateRoute>
                  <Layout>
                    <Attendances />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/staff/notifications"
              element={
                <PrivateRoute>
                  <Layout>
                    <StaffNotificationSettings />
                  </Layout>
                </PrivateRoute>
              }
            />
            {/* Redirect /menus to /mobile-order/products */}
            <Route
              path="/menus"
              element={<Navigate to="/mobile-order/products" replace />}
            />
            <Route
              path="/settings/shop"
              element={
                <PrivateRoute>
                  <Layout>
                    <ShopSettingsUnified />
                  </Layout>
                </PrivateRoute>
              }
            />
            {/* Redirect old homepage settings to unified shop settings */}
            <Route
              path="/settings/homepage"
              element={<Navigate to="/settings/shop" replace />}
            />
            <Route
              path="/settings/plan"
              element={
                <PrivateRoute>
                  <Layout>
                    <PlanSettings />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/sales-report"
              element={
                <PrivateRoute>
                  <Layout>
                    <SalesReport />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/all-shops"
              element={
                <PrivateRoute>
                  <Layout>
                    <AllShops />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/settings/time-charge"
              element={
                <PrivateRoute>
                  <Layout>
                    <TimeChargeSettings />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/settings/stripe-connect"
              element={
                <PrivateRoute>
                  <Layout>
                    <StripeConnectSettings />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/settings/mobile-order"
              element={
                <PrivateRoute>
                  <Layout>
                    <MobileOrderSettings />
                  </Layout>
                </PrivateRoute>
              }
            />

            {/* Mobile Order routes */}
            <Route
              path="/mobile-order/categories"
              element={
                <PrivateRoute>
                  <Layout>
                    <ProductCategories />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/mobile-order/options"
              element={
                <PrivateRoute>
                  <Layout>
                    <ProductOptions />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/mobile-order/products"
              element={
                <PrivateRoute>
                  <Layout>
                    <Products />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/mobile-order/tables"
              element={
                <PrivateRoute>
                  <Layout>
                    <Tables />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/mobile-order/printers"
              element={
                <PrivateRoute>
                  <Layout>
                    <Printers />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/mobile-order/plans"
              element={
                <PrivateRoute>
                  <Layout>
                    <Plans />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/mobile-order/orders"
              element={
                <PrivateRoute>
                  <Layout>
                    <Orders />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <PrivateRoute>
                  <Layout>
                    <Analytics />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/floor-map"
              element={
                <PrivateRoute>
                  <Layout>
                    <FloorMap />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/events"
              element={
                <PrivateRoute>
                  <Layout>
                    <Events />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/coupons"
              element={
                <PrivateRoute>
                  <Layout>
                    <Coupons />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/mobile-order/checkout"
              element={
                <PrivateRoute>
                  <Layout>
                    <Checkout />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/back-approval"
              element={
                <PrivateRoute>
                  <Layout>
                    <BackApproval />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/receivables"
              element={
                <PrivateRoute>
                  <Layout>
                    <Receivables />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/bottle-keeps"
              element={
                <PrivateRoute>
                  <Layout>
                    <BottleKeeps />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/proxy-order"
              element={
                <PrivateRoute>
                  <Layout>
                    <ProxyOrder />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/payroll"
              element={
                <PrivateRoute>
                  <Layout>
                    <Payroll />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/allowance-types"
              element={
                <PrivateRoute>
                  <Layout>
                    <AllowanceTypes />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/setup-check"
              element={
                <PrivateRoute>
                  <Layout>
                    <SetupCheck />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/payouts"
              element={
                <PrivateRoute>
                  <Layout>
                    <Payouts />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/blacklist"
              element={
                <PrivateRoute>
                  <Layout>
                    <Blacklist />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/migrate"
              element={
                <PrivateRoute>
                  <Layout>
                    <MigrateData />
                  </Layout>
                </PrivateRoute>
              }
            />

            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
                {/* どのルートにも当たらない場合。
                これが無いと画面が真っ白になり、原因が分からないまま「壊れている」ように見える。 */}
            <Route
              path="*"
              element={
                <PrivateRoute>
                  <Layout>
                    <NotFound />
                  </Layout>
                </PrivateRoute>
              }
            />
      </Routes>
          </Router>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
